import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Fade,
  Grow,
  Chip,
  useTheme,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import {
  Brain,
  MessageSquare,
  Smartphone,
  TrendingUp,
  TrendingDown,
  CreditCard,
  RefreshCw,
  Zap,
} from "lucide-react";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

const CreditStats = () => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    const types = ["AI", "WABA", "RCS Media"];
    const categories = ["recharge"];

    const promises = types.flatMap((type) =>
      categories.map((category) =>
        fetch(
          `${API_ENDPOINT}/credit/get-total-recharge-types?type=${type}&category=${category}`,
          {
            method: "GET",
            ...getHeader(),
          }
        )
          .then((res) => res.json())
          .then((data) => ({
            type: type.toLowerCase(),
            category,
            amount: data.totalAmount,
          }))
      )
    );

    try {
      const results = await Promise.all(promises);
      const newStats = {};
      results.forEach((result) => {
        newStats[`${result.type}-${result.category}`] = result.amount;
      });
      setStats(newStats);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch credit stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    const iconProps = { size: 28 };
    switch (type) {
      case "ai":
        return <Brain {...iconProps} />;
      case "waba":
        return <MessageSquare {...iconProps} />;
      case "rcs":
        return <Smartphone {...iconProps} />;
      default:
        return <Brain {...iconProps} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "ai":
        return theme.palette.primary.main;
      case "waba":
        return theme.palette.secondary.main;
      case "rcs":
        return theme.palette.success.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getCategoryIcon = (category) => {
    const iconProps = { size: 16 };
    return category === "recharge" ? (
      <TrendingUp {...iconProps} />
    ) : (
      <TrendingDown {...iconProps} />
    );
  };

  const formatAmount = (amount) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Zap size={32} color={theme.palette.primary.main} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            Credit Statistics
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {lastUpdated && (
            <Chip
              icon={<RefreshCw size={14} />}
              label={`Updated: ${lastUpdated.toLocaleTimeString()}`}
              variant="outlined"
              size="small"
            />
          )}
          <Button
            variant="outlined"
            onClick={fetchStats}
            startIcon={<RefreshCw size={18} />}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Loading State */}
      {loading && (
        <Fade in={loading}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <RefreshCw size={32} className="spin" />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Loading credit statistics...
            </Typography>
          </Box>
        </Fade>
      )}

      {/* Statistics Grid */}
      {!loading && (
        <Fade in={!loading}>
          <Box>
            <Grid container spacing={3}>
              {Object.entries(stats).map(([key, value], index) => {
                const [type, category] = key.split("-");
                const typeColor = getTypeColor(type);
                const isRecharge = category === "recharge";

                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Grow in timeout={index * 200}>
                      <Card
                        sx={{
                          height: "100%",
                          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
                          border: `1px solid ${theme.palette.divider}`,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: theme.shadows[8],
                            borderColor: typeColor,
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 2,
                                backgroundColor: `${typeColor}15`,
                                color: typeColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {getTypeIcon(type)}
                            </Box>
                            <Chip
                              icon={getCategoryIcon(category)}
                              label={category.toUpperCase()}
                              size="small"
                              variant="outlined"
                              color={isRecharge ? "success" : "error"}
                              sx={{ fontWeight: "bold" }}
                            />
                          </Box>

                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "bold",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              color: theme.palette.text.secondary,
                              mb: 1,
                            }}
                          >
                            {type} Credits
                          </Typography>

                          <Typography
                            variant="h3"
                            sx={{
                              fontWeight: "bold",
                              background: `linear-gradient(45deg, ${typeColor}, ${theme.palette.text.primary})`,
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              color: "transparent",
                              mb: 1,
                            }}
                          >
                            ₹{formatAmount(value)}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {isRecharge ? (
                              <>
                                <TrendingUp size={14} />
                                Total recharged amount
                              </>
                            ) : (
                              <>
                                <TrendingDown size={14} />
                                Total usage amount
                              </>
                            )}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grow>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Fade>
      )}

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Container>
  );
};
export default CreditStats;
